import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { usePermissions, type AuthRole } from "./hooks/usePermissions";

// Eager: marketing apex routes + auth flows. These are the surfaces an
// anonymous visitor lands on, so they ship in the initial bundle. Every
// admin/portal/customer/governance page below is lazy-loaded so the
// marketing visitor never downloads it.
import IndexPage from "./pages/IndexPage";
import InviteAcceptPage from "./pages/InviteAccept";
import CookieBanner from "./components/marketing/CookieBanner";

// Home (with SeracLogin, Turnstile, authApi etc.) is only rendered for
// /login, /register, /verify-email, etc. — never on the marketing apex,
// so it lives in its own lazy chunk.
const HomePage = lazy(() => import("./pages/Home"));
import Pricing from "./pages/Pricing";
import Roles from "./pages/Roles";
import Checkout from "./pages/Checkout";
import PaymentComplete from "./pages/billing/PaymentComplete";
import DeviceAuthorize from "./pages/DeviceAuthorize";
import SsoCallback from "./pages/auth/SsoCallback";

// Legal pages — lazy. They're public but rarely the landing page,
// and Privacy.tsx alone was 13 KiB of unused code in the marketing
// initial bundle.
const LegalPrivacy = lazy(() => import("./pages/legal/Privacy"));
const LegalTerms = lazy(() => import("./pages/legal/Terms"));
const LegalDPA = lazy(() => import("./pages/legal/DPA"));
const LegalCookies = lazy(() => import("./pages/legal/Cookies"));
const LegalSubProcessors = lazy(() => import("./pages/legal/SubProcessors"));

// Use cases (long-form SEO pillar pages) — public, lazy.
const UseCaseAiInServiceNow = lazy(() => import("./pages/use-cases/AiInServiceNow"));

// FAQ — public, lazy. Long page with FAQPage JSON-LD; not on the
// marketing critical path so it ships in its own chunk.
const Faq = lazy(() => import("./pages/Faq"));

// Blog (journal) — public, lazy. BlogPost handles slug routing internally.
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));

// Lazy: admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOps = lazy(() => import("./pages/admin/Ops"));
const AdminTelemetry = lazy(() => import("./pages/admin/Telemetry"));
const AdminAiCrawlers = lazy(() => import("./pages/admin/AiCrawlers"));
const AdminRevenue = lazy(() => import("./pages/admin/Revenue"));
const AdminAccounts = lazy(() => import("./pages/admin/Accounts"));
const AdminEmailLogs = lazy(() => import("./pages/admin/EmailLogs"));
const AdminSkillsReview = lazy(() => import("./pages/admin/SkillsReview"));
const AdminReporting = lazy(() => import("./pages/admin/Reporting"));
const AdminSupport = lazy(() => import("./pages/admin/Support"));

// Admin 404 — lives in AdminLayout so the "page not found" screen renders
// inside the admin shell (nav + header) instead of bouncing the user out.
const AdminNotFound = lazy(() =>
  import("./components/layout/AdminLayout").then((m) => ({ default: m.AdminNotFound }))
);

// Lazy: portal/customer pages
const CustomerOperations = lazy(() => import("./pages/customer/Operations"));
const PortalProfile = lazy(() => import("./pages/portal/Profile"));
const TeamMembers = lazy(() => import("./pages/portal/TeamMembers"));
const CustomerCredentials = lazy(() => import("./pages/customer/Credentials"));
const UserCredentials = lazy(() => import("./pages/customer/UserCredentials"));
const CustomerServiceNowInstances = lazy(() => import("./pages/customer/ServiceNowInstances"));
const CustomerToolPermissions = lazy(() => import("./pages/customer/ToolPermissions"));
const CustomerUsage = lazy(() => import("./pages/customer/Usage"));
const CustomerActivities = lazy(() => import("./pages/customer/Activities"));
const CustomerUpdateSets = lazy(() => import("./pages/customer/UpdateSets"));
const CustomerArtifactPreview = lazy(() => import("./pages/customer/ArtifactPreview"));
const CustomerInstanceMap = lazy(() => import("./pages/customer/InstanceMap"));
const CustomerNotificationSettings = lazy(() => import("./pages/customer/NotificationSettings"));
const CustomerBilling = lazy(() => import("./pages/customer/Billing"));
const LLMProviderSettings = lazy(() => import("./pages/customer/LLMProviderSettings"));
const CustomerSupport = lazy(() => import("./pages/customer/Support"));
const Chat = lazy(() => import("./pages/Chat"));
const SnowAppsHarness = lazy(() => import("./pages/dev/SnowAppsHarness"));

// Lazy: governance pages
const GovernanceHub = lazy(() => import("./pages/governance/GovernanceHub"));
const GovernancePolicies = lazy(() => import("./pages/governance/Policies"));
const GovernanceMultiInstance = lazy(() => import("./pages/governance/MultiInstance"));
const GovernanceAuditTrail = lazy(() => import("./pages/governance/AuditTrail"));
const GovernanceCodeIntel = lazy(() => import("./pages/governance/CodeIntelligence"));
const GovernanceSkills = lazy(() => import("./pages/governance/SkillsCatalog"));

/**
 * Protected route wrapper.
 *
 * Roles come in through `allowedRoles`. The `requireAdmin` / `requireMarketing`
 * booleans are gone with this wave: no route passed them any more, and
 * `requireAdmin` carried the branch that waved marketing through every admin
 * page — the exact boundary this wave draws. Bare <ProtectedRoute> still means
 * "any authenticated session".
 */
function ProtectedRoute({
  children,
  allowedRoles,
  requireSubscription,
  minTier,
}: {
  children: React.ReactNode;
  allowedRoles?: AuthRole[];
  requireSubscription?: boolean;
  /** Minimum hosted tier (ladder: free < solo < pro < team < enterprise).
      Below it, portal users bounce to billing. `"pro"` = consultant
      toolkit (code intelligence / upgrade simulator), `"enterprise"` =
      org-governance. */
  minTier?: 'solo' | 'pro' | 'team' | 'enterprise';
}) {
  const {
    isPortalAuthenticated,
    isSsoAuthenticated,
    isLoading,
  } = useAuth();

  const {
    role,
    hasActiveSubscription,
    hasTier,
  } = usePermissions();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 spinner" />
      </div>
    );
  }

  // New role-based check — if allowedRoles is provided, use it
  if (allowedRoles) {
    // Two different failures, two different answers. No session at all →
    // the login screen. A live session with the wrong role → the 403 inside
    // the admin shell, because sending a signed-in marketing user to
    // /admin/login shows them a prompt for an ADMIN_KEY they do not have and
    // looks exactly like the unexplained logout this wave removes. Only
    // admin-shell roles get that screen; a portal customer guessing an
    // /admin URL must not be handed the admin chrome.
    if (!role) {
      if (allowedRoles.includes('admin') || allowedRoles.includes('marketing')) {
        return <Navigate to="/admin/login" replace />;
      }
      return <Navigate to="/" replace />;
    }
    if (!allowedRoles.includes(role)) {
      return role === 'admin' || role === 'marketing'
        ? <AdminNotFound variant="forbidden" />
        : <Navigate to="/" replace />;
    }
    if (requireSubscription && isPortalAuthenticated && !hasActiveSubscription) {
      return <Navigate to="/portal/billing" replace />;
    }
    if (minTier && isPortalAuthenticated && !hasTier(minTier)) {
      return <Navigate to="/portal/billing" replace />;
    }
    return <>{children}</>;
  }

  // No allowedRoles: any authenticated session may pass, subject to the
  // subscription / tier gates below.

  // Subscription gating: portal users need active subscription for paid features
  if (requireSubscription && isPortalAuthenticated && !hasActiveSubscription) {
    return <Navigate to="/portal/billing" replace />;
  }

  // Tier gating: pages above the account's tier bounce to billing,
  // matching the hidden nav (CustomerLayout gates the same items via
  // hasTier). Code intelligence / upgrade simulator = minTier "pro"
  // (the reverse-trial resolves to pro, so trialing users get in);
  // org-governance = minTier "enterprise".
  if (minTier && isPortalAuthenticated && !hasTier(minTier)) {
    return <Navigate to="/portal/billing" replace />;
  }

  // General auth: must be authenticated via any method
  if (!isPortalAuthenticated && !isSsoAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function RouteFallback() {
  // Mirrors the spinner shown by ProtectedRoute while auth resolves —
  // keeps lazy-chunk loading visually identical to other deferred work.
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-2 spinner" />
    </div>
  );
}

export default function App() {
  return (
    <>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Home / Landing */}
      <Route path="/" element={<IndexPage />} />
      <Route path="/login" element={<HomePage />} />
      <Route path="/register" element={<HomePage />} />

      {/* Pricing page */}
      <Route path="/pricing" element={<Pricing />} />

      {/* Roles page */}
      <Route path="/roles" element={<Roles />} />

      {/* Use cases (public, SEO pillar) */}
      <Route path="/use-cases/ai-in-servicenow" element={<UseCaseAiInServiceNow />} />

      {/* FAQ (public, SEO support) */}
      <Route path="/faq" element={<Faq />} />

      {/* Blog (public) */}
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Legal pages (public) */}
      <Route path="/legal/privacy" element={<LegalPrivacy />} />
      <Route path="/legal/terms" element={<LegalTerms />} />
      <Route path="/legal/dpa" element={<LegalDPA />} />
      <Route path="/legal/cookies" element={<LegalCookies />} />
      <Route path="/legal/sub-processors" element={<LegalSubProcessors />} />

      {/* Checkout & Payment pages */}
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/billing/payment-complete" element={<PaymentComplete />} />

      {/* Email verification & magic link routes - handled by HomePage */}
      <Route path="/verify-email" element={<HomePage />} />
      <Route path="/auth/magic-link" element={<HomePage />} />
      <Route path="/auth/callback" element={<HomePage />} />
      <Route path="/auth/sso-callback" element={<SsoCallback />} />
      <Route path="/reset-password" element={<HomePage />} />
      <Route path="/invite/accept" element={<InviteAcceptPage />} />

      {/* ─── Admin Routes ────────────────────────────────────────
          Every /admin route is guarded by allowedRoles, never by the
          legacy requireAdmin/requireMarketing booleans: requireAdmin
          let marketing users through explicitly, so the two were
          indistinguishable in practice. allowedRoles is the only guard
          that actually refuses. The split mirrors the nav in
          AdminLayout — but the nav only hides, the guard denies, and
          the backend denies again.
          - admin + marketing: cockpit, accounts (users and orgs both
            redirect there), support, reporting, AI crawlers
          - admin only: e-mail (bodies carry login tokens), revenue,
            skills review, ops, telemetry */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin', 'marketing']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'marketing']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      {/* Retired pages — keep the paths alive so old bookmarks land on the
          cockpit instead of the 404. Themes read a directory that isn't in
          the Docker image; the waitlist table never held a single row. */}
      <Route path="/admin/themes" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/waitlist" element={<Navigate to="/admin/dashboard" replace />} />
      <Route
        path="/admin/accounts"
        element={
          <ProtectedRoute allowedRoles={['admin', 'marketing']}>
            <AdminAccounts />
          </ProtectedRoute>
        }
      />
      {/* /admin/orgs showed the org half of the same reality Accounts now
          shows whole, and every action it carried (elevate, demote, the org
          detail panel) moved across. The path stays alive so bookmarks land
          on the cockpit instead of the 404. */}
      <Route path="/admin/orgs" element={<Navigate to="/admin/accounts" replace />} />
      {/* /admin/users is gone too. Everything it held alone now lives on
          Accounts: the `marketing` portal role and the warning that goes with
          it, a member's name, emailVerified and onboardingCompleted, the
          per-account post-trial module choice AND the cross-account
          popularity bar above the list, server-side sorting, impersonating a
          non-owner, and removing a single non-owner member. Two things were
          repaired on the way across rather than copied: search now also
          matches non-owner member addresses, and a row carries a "marketing
          access" tag so the platform role is auditable without expanding
          every organisation. Same reason as /admin/orgs for keeping the path:
          bookmarks land on the cockpit, not on a 404. */}
      <Route path="/admin/users" element={<Navigate to="/admin/accounts" replace />} />
      <Route
        path="/admin/ops"
        element={
          /* Admin-only: this page reports tenant counts, process internals,
             open error reports and recent 5xx bodies. Marketing would get a
             page whose every card 403s, so the guard refuses instead. */
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminOps />
          </ProtectedRoute>
        }
      />
      {/* /admin/monitoring is what this page was called while it showed health
          checks and an in-memory request counter and nothing else. Ops is the
          same page with the four lying checks repaired and the three lists it
          never had (open errors, recent 5xx, mail delivery) beside them, so
          the old path redirects rather than 404s — it was in bookmarks and in
          the muscle memory of everyone who ever debugged a deploy. */}
      <Route path="/admin/monitoring" element={<Navigate to="/admin/ops" replace />} />
      <Route
        path="/admin/telemetry"
        element={
          /* Off the nav as of wave 4b and reachable from Reporting > Usage.
             Admin-only now: this is an archive of anonymous CLI pings with
             three-week gaps in it, not a dashboard, and `exit_error_message`
             carries Windows account names belonging to real users. Neither is
             something a read-only marketing session needs to hold. */
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminTelemetry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-crawlers"
        element={
          <ProtectedRoute allowedRoles={['admin', 'marketing']}>
            <AdminAiCrawlers />
          </ProtectedRoute>
        }
      />
      {/* Everything about money. Admin-only, like the three pages it
          replaces — GET /api/admin/revenue is behind requireRoles('admin')
          server-side, so a marketing session would get a page of 403s. */}
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminRevenue />
          </ProtectedRoute>
        }
      />
      {/* Wave 5 — the three PLATFORM money pages became /admin/revenue.
          Redirects, not deletions: /admin/upgrade-requests is the link in the
          admin notification mail sent on every new enterprise request, and the
          other two are four months of bookmarks. Without these the catch-all
          sends an admin to "/", which on dashboard.serac.build is the login
          screen and reads as an unexplained logout.
          /admin/invoices/:id drops its id — there is no detail view to land
          on; the one invoice in the table is a row on the revenue page. */}
      <Route path="/admin/billing" element={<Navigate to="/admin/revenue" replace />} />
      <Route path="/admin/invoices" element={<Navigate to="/admin/revenue" replace />} />
      <Route path="/admin/invoices/:id" element={<Navigate to="/admin/revenue" replace />} />
      <Route path="/admin/upgrade-requests" element={<Navigate to="/admin/revenue" replace />} />
      {/* Admin-only: the logs render the full HTML body of every sent mail,
          including magic-link and verification tokens. */}
      <Route
        path="/admin/email-logs"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminEmailLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute allowedRoles={['admin', 'marketing']}>
            <AdminSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/skills-review"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSkillsReview />
          </ProtectedRoute>
        }
      />

      {/* Marketing / Reporting */}
      {/* Marketing login redirect — the dedicated page is gone (MARKETING_KEY
          is unset in prod, so it could never succeed). Marketing signs in with
          a portal email, NOT the admin key, so /admin/login is a dead end for
          them: send them home, where Home routes a live session onward. */}
      <Route path="/admin/marketing-login" element={<Navigate to="/" replace />} />
      <Route
        path="/admin/reporting"
        element={
          <ProtectedRoute allowedRoles={['admin', 'marketing']}>
            <AdminReporting />
          </ProtectedRoute>
        }
      />

      {/* Admin 404 — last /admin route. Anything under /admin that didn't
          match above renders the not-found screen inside the admin shell.
          Same guard as the rest, so a logged-out visitor is sent to
          /admin/login rather than shown the dashboard chrome. */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={['admin', 'marketing']}>
            <AdminNotFound />
          </ProtectedRoute>
        }
      />

      {/* Device Authorization (CLI) - Public route, will redirect to login if needed */}
      <Route path="/device/authorize" element={<DeviceAuthorize />} />

      {/* Customer Portal Routes - Redirect /portal/login to home */}
      <Route path="/portal/login" element={<Navigate to="/" replace />} />
      {/* The Operations Board is the front door, and its route MUST stay above
          the two redirects that point at it — a redirect to a path with no
          route falls through to the `*` catch-all, which sends the user to `/`,
          which sends a signed-in user back to /portal/dashboard. That is an
          infinite loop through the login screen and a blank page.

          No requireSubscription and no minTier, matching /portal/update-sets
          and /portal/instance-map below. /api/operations sits behind
          portalSubscriptionGuard, whose ACTIVE_STATUSES includes 'free'
          (module-access.ts) — and a self-serve signup never gets a
          subscriptions row, which the guard also resolves to 'free'. Every
          number on this page is about the customer's ServiceNow instance, not
          about what they bought. */}
      <Route
        path="/portal/operations"
        element={
          <ProtectedRoute>
            <CustomerOperations />
          </ProtectedRoute>
        }
      />
      {/* /portal/dashboard is a PERMANENT redirect and must never be deleted:
          eleven call sites hardcode it, three of them backend senders that put
          it in live email. The dashboard it used to render was account metrics;
          the board reports the customer's instance instead. */}
      <Route path="/portal" element={<Navigate to="/portal/operations" replace />} />
      <Route path="/portal/dashboard" element={<Navigate to="/portal/operations" replace />} />
      {/* No requireSubscription: /api/credentials/* is mounted behind
          portalSubscriptionGuard, whose ACTIVE_STATUSES includes 'free' — and a
          self-serve signup never gets a subscriptions row, which the guard also
          resolves to 'free'. So the backend answers a Free org's credential
          calls with 200 (verified live on prod) while this guard bounced them
          to /portal/billing: the tab promised a page the router refused. The
          paid wall for the integrations themselves is enforced where they run —
          CATEGORY_FEATURES in services/tool-search.ts — not on storing a
          credential. */}
      <Route
        path="/portal/credentials"
        element={
          <ProtectedRoute>
            <CustomerCredentials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/my-credentials"
        element={
          <ProtectedRoute>
            <UserCredentials />
          </ProtectedRoute>
        }
      />
      {/* No requireSubscription: connecting the first ServiceNow instance is a
          FREE-tier capability (TIER_PRESETS.free.maxInstances = 1), and the
          instance cap is enforced server-side by services/instance-cap.ts, not
          by this route. The guard sent every subscription-less account to
          /portal/billing — including all 37 recipients of the connect-instance
          nudge, whose only call to action is this page, and the verification
          reminder's promise that a verified account can connect an instance
          right away. It also blocked the one screen a free account needs before
          the product does anything at all. */}
      <Route
        path="/portal/servicenow"
        element={
          <ProtectedRoute>
            <CustomerServiceNowInstances />
          </ProtectedRoute>
        }
      />
      {/* No requireSubscription: the only entry point is the per-instance
          button on /portal/servicenow, which is itself open to Free, and the
          page does two read-only calls — a proxy on the public roles manifest
          and a sys_user_has_role check on the account's own instance, which
          /roles already scopes to the caller's organization. The API behind it
          (portalSubscriptionGuard) answers these accounts today, so the guard
          only produced a silent bounce to billing from a screen that has no
          nav item to come back from. */}
      <Route
        path="/portal/tool-permissions"
        element={
          <ProtectedRoute>
            <CustomerToolPermissions />
          </ProtectedRoute>
        }
      />
      {/* No requireSubscription: CustomerLayout puts Usage in the nav for
          `isFree`, and /api/customer/usage answers those accounts — the guard
          only made the nav item bounce to billing without saying why. Usage is
          neither consultant toolkit nor governance; the wall is scale. */}
      <Route
        path="/portal/usage"
        element={
          <ProtectedRoute>
            <CustomerUsage />
          </ProtectedRoute>
        }
      />
      {/* /portal/users is gone for good: it was the license-key-era seat
          table, and seat invites moved to the organization model (see the 410
          comment on POST /api/customer/invite-user). /portal/team is the
          living page and nothing links at the old path any more —
          AdminDashboard's two links ("Manage users" button + quick action)
          now point at /portal/team — so the redirect stub is dropped too. */}
      {/* No requireSubscription: /api/portal/team/* carries no subscription
          guard and answers an org without a subscription row (seat caps fall
          back to 1 developer seat), so the gate only hid the UI from Free
          accounts that the backend was already serving. The wall is scale —
          seats — and that is enforced server-side on invite. */}
      <Route
        path="/portal/team"
        element={
          <ProtectedRoute>
            <TeamMembers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/profile"
        element={
          <ProtectedRoute>
            <PortalProfile />
          </ProtectedRoute>
        }
      />
      {/* No requireSubscription on the next four, same reason as
          /portal/servicenow above: the wall is SCALE, not capability. Activity
          log, update sets, artifact preview and instance map are all views on
          what the free agent already did on its one instance — CustomerLayout
          puts every one of them in the nav for `isFree`, and the instance map
          is a headline on the Free plan card. The guard turned each of those
          nav clicks into a silent <Navigate> to /portal/billing for the 52
          organisations without a subscription row. What Free is actually
          limited to (1 instance, 7 days of history) is enforced server-side,
          not by this route. */}
      <Route
        path="/portal/activities"
        element={
          <ProtectedRoute>
            <CustomerActivities />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/update-sets"
        element={
          <ProtectedRoute>
            <CustomerUpdateSets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/artifact-preview"
        element={
          <ProtectedRoute>
            <CustomerArtifactPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/instance-map"
        element={
          <ProtectedRoute>
            <CustomerInstanceMap />
          </ProtectedRoute>
        }
      />

      {/* ─── Governance (modular) ─────────────────────────────
          Tier ladder, matching TIER_PRESETS in the backend:
          - hub / policies / multi-instance / audit → Enterprise-only
            (hidden from nav AND unreachable by deep link below it,
            incl. the full-Pro reverse-trial)
          - code-intelligence (+ upgrade simulator) → Pro and up:
            consultant toolkit, so trial (= full Pro) gets it
          - skills catalog → every paid tier (Solo and up) */}
      <Route
        path="/governance"
        element={
          <ProtectedRoute requireSubscription minTier="enterprise">
            <GovernanceHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/governance/policies"
        element={
          <ProtectedRoute requireSubscription minTier="enterprise">
            <GovernancePolicies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/governance/multi-instance"
        element={
          <ProtectedRoute requireSubscription minTier="enterprise">
            <GovernanceMultiInstance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/governance/audit"
        element={
          <ProtectedRoute requireSubscription minTier="enterprise">
            <GovernanceAuditTrail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/governance/code-intelligence"
        element={
          <ProtectedRoute requireSubscription minTier="pro">
            <GovernanceCodeIntel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/governance/skills"
        element={
          <ProtectedRoute requireSubscription>
            <GovernanceSkills />
          </ProtectedRoute>
        }
      />
      {/* No requireSubscription: the notification bell renders unconditionally
          in the sidebar and the top bar for every portal user, and its "View
          all" goes here — hiding the nav item would not have closed that door.
          /api/agent/notifications/* already answers Free accounts. */}
      <Route
        path="/portal/notifications"
        element={
          <ProtectedRoute>
            <CustomerNotificationSettings />
          </ProtectedRoute>
        }
      />
      {/* Billing is the upgrade page AND the redirect target for requireSubscription
          routes, so it must NOT itself require a subscription — a Free user
          (hasActiveSubscription=false) would redirect /portal/billing → /portal/billing
          forever (infinite loop → blank page). */}
      <Route
        path="/portal/billing"
        element={
          <ProtectedRoute>
            <CustomerBilling />
          </ProtectedRoute>
        }
      />
      {/* /portal/sso and /portal/branding are gone. Both were license-key-era
          white-label screens with nothing behind them: the SSO page reads a
          `customerSession` that is a hard-coded null stub since license-key
          auth was removed, and PUT /api/customer/branding already answers
          every portal user with a 403 pointing at billing. No page linked to
          /portal/branding at all; /portal/sso only had the sidebar entry,
          which is removed with them. Deep links now fall through to the
          catch-all. */}
      <Route
        path="/portal/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/chat/:sessionId"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/providers"
        element={
          <ProtectedRoute>
            <LLMProviderSettings />
          </ProtectedRoute>
        }
      />
      {/* Legacy path — redirect to new /portal/providers URL */}
      <Route
        path="/portal/llm-providers"
        element={<Navigate to="/portal/providers" replace />}
      />
      {/* Connections shorthand — sidebar entry lands on default tab (AI Providers) */}
      <Route
        path="/portal/connections"
        element={<Navigate to="/portal/providers" replace />}
      />
      <Route
        path="/portal/support"
        element={
          <ProtectedRoute>
            <CustomerSupport />
          </ProtectedRoute>
        }
      />

      {/* Dev-only harness for visually checking chat snow-app components. */}
      {import.meta.env.DEV && (
        <Route path="/_dev/snow-apps" element={<SnowAppsHarness />} />
      )}

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    <CookieBanner />
    </>
  );
}
